import { useEffect } from "react";
import { Container, Grid } from "@mui/material";
import { useFindingRiskFactors } from "~/pages/space/security/components/RiskFactors/hooks/useFindingRiskFactors";
import {
  AggregateScoreOrderField,
  AggregateScoreType,
  FindingType,
  NotFoundError,
  OrderDirection,
  RequestError,
  ScoreState,
  ScoreStateFilter,
  UiPage,
  useGetSpaceSoftwareQuery,
} from "~/operations";
import { Space } from "~/lib/types";
import { useSearchParams } from "react-router-dom";
import { StatsProps } from "~/components/DetailsPage/components/Stats/Stats";
import { FlagOutlined, Radar } from "@mui/icons-material";
import { MessageSeverity, WarningMessage } from "~/components/user-warning";
import { DynamicIcon } from "~/components/icons/DynamicIcon";
import { SoftwareRiskFactors } from "~/pages/space/software/Software/components/SoftwareRiskFactors";
import { SoftwareVersions } from "~/pages/space/software/Software/components/SoftwareVersions";
import { LoadingPage } from "~/components/loading";
import { pluralize } from "~/lib/pluralize";
import { SoftwareAdvisories } from "./components/SoftwareAdvisories";
import { SoftwareVulnerabilities } from "./components/SoftwareVulnerabilities";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { DetailPageTopSection } from "~/components/DetailPageLayouts/DetailPageTopSection";
import { ScoreBlock } from "../../security/components/Check/ScoreBlock";
import {
  ExposedAssets,
  AssetContextualLinkType,
} from "~/pages/space/vulnerabilities/components/exposed-assets";
import { SCORE_STATE_FILTER_KEY } from "~/pages/inventory/asset/useAssetVulnerabilitiesFilterContext";
import { PackageIcon, VersionsIcon } from "~/icons";
import { useReportViewed } from "~/hooks/useReportViewed";
import { FilePath } from "~/components/Remediation/FilePath/FilePath";

type SoftwareProps = { space: Space; scope: SpaceOrWorkspaceScope };

export const Software = ({ space, scope }: SoftwareProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const genericFindingMrn = searchParams.get("genericFindingMrn") || "";
  const versionedFindingMrn = searchParams.get("versionedFindingMrn") || "";
  const isVersionedSoftware = versionedFindingMrn.length > 0;

  if (!genericFindingMrn && !versionedFindingMrn) {
    return (
      <Grid size={12}>
        <WarningMessage
          severity={MessageSeverity.Error}
          msg="No Software MRN provided!"
        />
      </Grid>
    );
  }

  const {
    data: versionedSoftwareDetailsData,
    loading: versionedSoftwareDetailsLoading,
    error: versionedSoftwareAggDataError,
  } = useGetSpaceSoftwareQuery({
    variables: {
      entityMrn: scope.mrn || "",
      orderBy: {
        field: AggregateScoreOrderField.Rank,
        direction: OrderDirection.Asc,
      },
      filter: {
        softwareFindingMrn: versionedFindingMrn || genericFindingMrn,
        scoreType: AggregateScoreType.VersionedSoftware,
      },
    },
    skip: !scope.mrn,
  });

  const {
    data: genericSoftwareDetailsData,
    loading: genericSoftwareDetailsLoading,
    error: genericSoftwareAggDataError,
  } = useGetSpaceSoftwareQuery({
    variables: {
      entityMrn: scope.mrn || "",
      orderBy: {
        field: AggregateScoreOrderField.Rank,
        direction: OrderDirection.Asc,
      },
      filter: {
        findingMrn: genericFindingMrn,
        scoreType: AggregateScoreType.Software,
      },
    },
    skip: Boolean(versionedFindingMrn) || !scope.mrn,
  });

  const {
    riskFactorsWithDocs,
    riskFactors,
    loading: riskFactorsLoading,
  } = useFindingRiskFactors({
    spaceMrn: space.mrn,
    findingMrn: versionedFindingMrn || genericFindingMrn,
    scoreType: isVersionedSoftware
      ? AggregateScoreType.VersionedSoftware
      : AggregateScoreType.Software,
    onlyAffected: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [genericFindingMrn, versionedFindingMrn]);

  useEffect(() => {
    searchParams.set(SCORE_STATE_FILTER_KEY, ScoreState.Open);
    setSearchParams(searchParams, { replace: true });
  }, []);

  // Send report viewed mutation on mount and when the pathname changes
  useReportViewed({
    page: UiPage.Finding,
    pageLoaded: !!(genericFindingMrn || versionedFindingMrn),
    assetMrn: versionedFindingMrn || genericFindingMrn,
    spaceMrn: space.mrn,
    tab: "software",
  });

  const genericSoftwareNotFoundError =
    genericSoftwareDetailsData?.aggregateScores?.__typename === "NotFoundError"
      ? genericSoftwareDetailsData?.aggregateScores
      : undefined;

  const genericSoftwareRequestError =
    genericSoftwareDetailsData?.aggregateScores?.__typename === "RequestError"
      ? genericSoftwareDetailsData?.aggregateScores
      : undefined;

  const versionedSoftwareNotFoundError =
    versionedSoftwareDetailsData?.aggregateScores?.__typename ===
    "NotFoundError"
      ? versionedSoftwareDetailsData?.aggregateScores
      : undefined;

  const versionedSoftwareRequestError =
    versionedSoftwareDetailsData?.aggregateScores?.__typename === "RequestError"
      ? versionedSoftwareDetailsData?.aggregateScores
      : undefined;

  const loading =
    genericSoftwareDetailsLoading ||
    versionedSoftwareDetailsLoading ||
    riskFactorsLoading;

  if (loading) {
    return <LoadingPage what="Software" />;
  }

  if (
    genericSoftwareRequestError ||
    versionedSoftwareRequestError ||
    versionedSoftwareAggDataError ||
    genericSoftwareAggDataError
  ) {
    const error = genericSoftwareRequestError || versionedSoftwareRequestError;
    return (
      <Grid size={12}>
        <WarningMessage
          severity={MessageSeverity.Error}
          msg={(error as RequestError).message}
        />
      </Grid>
    );
  }

  if (versionedSoftwareNotFoundError || genericSoftwareNotFoundError) {
    const error =
      versionedSoftwareNotFoundError || genericSoftwareNotFoundError;
    return (
      <Grid size={12}>
        <WarningMessage
          severity={MessageSeverity.Error}
          msg={(error as NotFoundError).message}
        />
      </Grid>
    );
  }

  const versionedSoftwareScores =
    versionedSoftwareDetailsData?.aggregateScores?.__typename ===
    "AggregateScoresConnection"
      ? versionedSoftwareDetailsData.aggregateScores
      : undefined;

  const genericSoftwareScores =
    genericSoftwareDetailsData?.aggregateScores?.__typename ===
    "AggregateScoresConnection"
      ? genericSoftwareDetailsData.aggregateScores
      : undefined;

  const softwareEdges = isVersionedSoftware
    ? versionedSoftwareScores?.edges
    : genericSoftwareScores?.edges;
  const software = softwareEdges?.[0]?.node;

  const findingMrn = isVersionedSoftware
    ? versionedFindingMrn
    : genericFindingMrn;

  const version = isVersionedSoftware
    ? versionedFindingMrn.split("/").pop()
    : null;

  const stats: StatsProps["stats"] =
    [
      {
        label: "Scanned",
        value: software?.versionDistribution?.assets?.toString() || "---",
        icon: <Radar fontSize="inherit" />,
      },
      {
        label: "Installs",
        value:
          software?.versionDistribution?.installations?.toString() || "---",
        icon: <FlagOutlined fontSize="inherit" />,
        onClick: () => {
          document
            .querySelector(`#software-assets`)
            ?.scrollIntoView({ behavior: "smooth" });
        },
      },
      !isVersionedSoftware
        ? {
            label: pluralize(
              "Version",
              genericSoftwareScores?.edges?.[0]?.node?.versionDistribution
                ?.distribution?.length || 0,
            ),
            value:
              genericSoftwareScores?.edges?.[0]?.node?.versionDistribution?.distribution?.length?.toString() ||
              "---",
            icon: <VersionsIcon fontSize="inherit" />,
            onClick: () => {
              document
                .querySelector(`#software-versions`)
                ?.scrollIntoView({ behavior: "smooth" });
            },
          }
        : null,
    ].flatMap((e) => e ?? []) || [];

  if (!software) {
    return (
      <Grid size={{ xs: 12 }}>
        <WarningMessage
          severity={MessageSeverity.Error}
          msg={"Software Not Found!"}
        />
      </Grid>
    );
  }

  const baseMrn = isVersionedSoftware
    ? versionedFindingMrn.split("/").slice(0, -1).join("/")
    : genericFindingMrn;

  const filter = {
    types: [FindingType.Package],
    state: ScoreStateFilter.All,
    mrn: baseMrn,
    ...(isVersionedSoftware && version
      ? { package: { packageVersion: version } }
      : {}),
  };

  return (
    <>
      <DetailPageTopSection
        content={{
          triageBar: {
            title: software?.title || "",
            fallbackPath: `/space/inventory/software?${scope.params}`,
            titleIcon: (
              <DynamicIcon
                iconId={software?.iconId}
                defaultIcon={PackageIcon}
                size={40}
              />
            ),
            isLoading: loading,
            subheaders: [
              {
                id: "created",
                label: "Created",
                value: software?.firstDetectedAt,
              },
              {
                id: "lastModified",
                label: "Last Modified",
                value: software?.lastScannedAt,
              },
            ],
          },
          stats: stats,
          summary: software.description
            ? software.description
            : "There is no description available for this package.",
          right: (
            <ScoreBlock
              mainScore={software?.riskValue}
              epssScore={software?.epss}
              cvssScore={software?.cvss}
              riskFactors={software?.riskFactors}
              rating={software?.rating}
              blastRadius={software?.blastRadius}
              hasScore={Boolean(software)}
              hasError={false}
            />
          ),
        }}
      />
      <Container sx={{ pt: 3 }}>
        {riskFactorsWithDocs.length > 0 && (
          <SoftwareRiskFactors
            riskFactorsWithDocs={riskFactorsWithDocs}
            cvssScore={software?.cvss}
          />
        )}

        <FilePath
          scopeMrn={scope.mrn}
          findingMrn={findingMrn}
          label="Found here:"
        />

        {!isVersionedSoftware && (
          <SoftwareVersions
            space={space}
            scope={scope}
            loading={versionedSoftwareDetailsLoading}
            edges={versionedSoftwareScores?.edges}
            riskFactors={riskFactors}
            findingMrn={genericFindingMrn}
            versionDistribution={
              isVersionedSoftware
                ? versionedSoftwareScores?.edges?.[0]?.node?.versionDistribution
                : genericSoftwareScores?.edges?.[0]?.node?.versionDistribution
            }
            showVersionDistribution={!isVersionedSoftware}
          />
        )}

        <ExposedAssets
          scope={scope}
          contextId={
            (baseMrn.includes("/name/")
              ? baseMrn.split("/name/").pop()
              : undefined) || software?.title
          }
          urlContextType={AssetContextualLinkType.Software}
          filter={filter}
          emptyStateMessage="There are currently no exposed assets for this package."
        />

        {/*<SoftwareAssets
        scope={scope}
        findingMrn={
          isVersionedSoftware ? versionedFindingMrn : genericFindingMrn
        }
        version={
          isVersionedSoftware ? versionedFindingMrn.split("/").pop() : null
        }
        riskFactors={riskFactors}
        softwareTitle={software?.title}
      />*/}

        {isVersionedSoftware && (
          <SoftwareVulnerabilities
            space={space}
            scope={scope}
            findingMrn={versionedFindingMrn || genericFindingMrn}
          />
        )}

        {isVersionedSoftware && (
          <SoftwareAdvisories
            space={space}
            scope={scope}
            findingMrn={versionedFindingMrn || genericFindingMrn}
          />
        )}
      </Container>
    </>
  );
};
